/**
 * 数据校验工具集
 * 根据设计规范实现各种数据类型的校验规则
 */

/**
 * 文本类型校验
 */
export const textValidators = {
  /**
   * 短文本校验
   * @param value 输入值
   * @returns 校验结果
   */
  validateShortText(value: string): { valid: boolean; message?: string } {
    if (value.length > 50) {
      return { valid: false, message: "短文本不能超过50个字符" };
    }
    return { valid: true };
  },

  /**
   * 长文本校验
   * @param value 输入值
   * @returns 校验结果
   */
  validateLongText(value: string): { valid: boolean; message?: string } {
    // 长文本不限制长度
    return { valid: true };
  },

  /**
   * 正则文本校验（身份证号）
   * @param value 输入值
   * @returns 校验结果
   */
  validateRegexText(value: string): { valid: boolean; message?: string } {
    // 身份证号校验正则（18位，最后一位可能是X）
    const idCardRegex =
      /^[1-9]\d{5}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/;
    if (!idCardRegex.test(value)) {
      return { valid: false, message: "请输入有效的身份证号码" };
    }

    // 校验码验证
    // if (!validateIDCardCheckDigit(value)) {
    //   return { valid: false, message: "身份证号码校验失败" };
    // }

    return { valid: true };
  },
};

/**
 * 数值类型校验
 */
export const numberValidators = {
  /**
   * 整数校验
   * @param value 输入值
   * @param options 配置项
   * @returns 校验结果
   */
  validateInteger(
    value: number | string,
    options: { min?: number; max?: number } = {}
  ): { valid: boolean; message?: string } {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num) || !Number.isInteger(num)) {
      return { valid: false, message: "请输入有效的整数" };
    }

    if (options.min !== undefined && num < options.min) {
      return { valid: false, message: `数值不能小于${options.min}` };
    }

    if (options.max !== undefined && num > options.max) {
      return { valid: false, message: `数值不能大于${options.max}` };
    }

    return { valid: true };
  },

  /**
   * 浮点数校验
   * @param value 输入值
   * @param options 配置项
   * @returns 校验结果
   */
  validateFloat(
    value: number | string,
    options: { min?: number; max?: number; decimalPlaces?: number } = {}
  ): { valid: boolean; message?: string } {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) {
      return { valid: false, message: "请输入有效的数字" };
    }

    if (options.min !== undefined && num < options.min) {
      return { valid: false, message: `数值不能小于${options.min}` };
    }

    if (options.max !== undefined && num > options.max) {
      return { valid: false, message: `数值不能大于${options.max}` };
    }

    if (options.decimalPlaces !== undefined) {
      const decimalPart = value.toString().split(".")[1] || "";
      if (decimalPart.length > options.decimalPlaces) {
        return {
          valid: false,
          message: `最多支持${options.decimalPlaces}位小数`,
        };
      }
    }

    return { valid: true };
  },
};

/**
 * 其他数据类型校验
 */
export const otherValidators = {
  /**
   * 邮箱校验
   */
  validateEmail(value: string): { valid: boolean; message?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return { valid: false, message: "请输入有效的邮箱地址" };
    }
    return { valid: true };
  },

  /**
   * 手机号校验
   */
  validatePhone(value: string): { valid: boolean; message?: string } {
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(value)) {
      return { valid: false, message: "请输入有效的手机号码" };
    }
    return { valid: true };
  },
};

/**
 * 身份证校验码验证
 * @param idCard 身份证号
 * @returns 是否通过校验
 */
function validateIDCardCheckDigit(idCard: string): boolean {
  if (idCard.length !== 18) return false;

  // 加权因子
  const weightFactors = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
  // 校验码
  const checkCodes = ["1", "0", "X", "9", "8", "7", "6", "5", "4", "3", "2"];

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    sum += parseInt(idCard.charAt(i)) * weightFactors[i];
  }

  const mod = sum % 11;
  const checkCode = checkCodes[mod];

  return idCard.charAt(17).toUpperCase() === checkCode;
}

/**
 * 表单字段校验器
 * @param type 字段类型
 * @param value 字段值
 * @param options 校验选项
 * @returns 校验结果
 */
export function validateField(
  type:
    | "shortText"
    | "longText"
    | "regexText"
    | "integer"
    | "float"
    | "email"
    | "phone",
  value: any,
  options: Record<string, any> = {}
): { valid: boolean; message?: string } {
  switch (type) {
    case "shortText":
      return textValidators.validateShortText(String(value));
    case "longText":
      return textValidators.validateLongText(String(value));
    case "regexText":
      return textValidators.validateRegexText(String(value));
    case "integer":
      return numberValidators.validateInteger(value, options);
    case "float":
      return numberValidators.validateFloat(value, options);
    case "email":
      return otherValidators.validateEmail(String(value));
    case "phone":
      return otherValidators.validatePhone(String(value));
    default:
      return { valid: false, message: "未知的校验类型" };
  }
}

/**
 * 表单验证器
 * @param rules 验证规则
 * @param values 表单值
 * @returns 错误信息对象
 */
export function validateForm(
  rules: Record<
    string,
    { type: string; required?: boolean; message?: string; [key: string]: any }
  >,
  values: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};

  Object.entries(rules).forEach(([field, rule]) => {
    const value = values[field];
    const { type, required = true, message, ...options } = rule;

    // 检查必填项
    if (required && (value === undefined || value === null || value === "")) {
      errors[field] = message || `${field}是必填项`;
      return;
    }

    // 非必填且为空时跳过其他校验
    if (!required && (value === undefined || value === null || value === "")) {
      return;
    }

    // 执行类型校验
    const result = validateField(type as any, value, options);
    if (!result.valid) {
      errors[field] = message || result.message || `${field}格式不正确`;
    }
  });

  return errors;
}
export const validatePhoneNumber = (phone: string): boolean => {
  return /^1[3-9]\d{9}$/.test(phone);
};

export const validatePassword = (password: string): boolean => {
  return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(password);
};
